import { Trans } from '@lingui/macro'
import { useWeb3React } from '@web3-react/core'
import { ButtonPurple } from 'components/Button'
import Loader from 'components/Icons/LoadingSpinner'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Twitter } from 'react-feather'
import { useLocation } from 'react-router-dom'
import { useTwitterIsBound } from 'state/competition/hooks'
import { updateTwitterBind } from 'state/competition/reducer'
import { useAppDispatch } from 'state/hooks'
import { Status } from 'state/interfaces'
import styled from 'styled-components/macro'

import useDebounce from '../../../hooks/useDebounce'
import useParsedQueryString from '../../../hooks/useParsedQueryString'
import { nowInSeconds } from '../../../utils/date'
import { agent } from '../api/agent'
import { ITwitter } from '../api/interfaces/twitter.interface'
import { useTwitterAuth } from '../hooks/useTwitterAuth'

const Wrapper = styled.div`
  position: absolute;
  z-index: 20;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  justify-content: center;
  background: #181337;
  border-radius: 12px;
  opacity: 0.95;
`

const Label = styled.div`
  font-size: 16px;
  line-height: 24px;
  color: #ffffff;
`

const BindTwitter = () => {
  const location = useLocation()
  const cb = useMemo(() => location.pathname.endsWith('/cb'), [location])
  const { provider, account } = useWeb3React()
  const { status, isTwitterBound, username } = useTwitterIsBound(account)
  const dispatch = useAppDispatch()
  const queryString = useParsedQueryString()
  const [req, setReq] = useState(false)
  const [confirmation, setConfirmation] = useState<{ twitter: ITwitter; state: string } | null>(null)
  const [twitters, setTwitters] = useState<{ [account: string]: string }>({})
  const reqSign = useRef(false)

  const allINeed = useMemo(() => {
    return { cb, queryString, account, req }
  }, [cb, queryString, account, req])

  const debounced = useDebounce(allINeed, 300)

  const signAccount = async () => {
    if (!account || !isTwitterBound || username || !provider || reqSign.current) return
    reqSign.current = true

    try {
      const timestamp = nowInSeconds()
      const signature = await provider.getSigner().signMessage(
        JSON.stringify({
          timestamp,
        })
      )
      dispatch(updateTwitterBind({ isBound: true, status: Status.PENDING, account }))
      const res = await agent.competition.signAccount(account, timestamp.toString(), signature)
      dispatch(updateTwitterBind({ isBound: true, status: Status.SUCCESS, account, username: res.twitter }))
    } catch (e) {
      dispatch(updateTwitterBind({ isBound: true, status: Status.ERROR, account }))
      console.error(e)
    }
  }

  useEffect(() => {
    signAccount()
  }, [account, isTwitterBound, username, provider])

  useEffect(() => {
    const { cb, queryString, req } = debounced

    if (cb && !req) {
      const { twitter, wallet } = queryString
      if (
        twitter &&
        wallet &&
        typeof twitter === 'string' &&
        typeof wallet === 'string' &&
        account &&
        account.toLowerCase() === wallet.toLowerCase()
      ) {
        setReq(true)
        if (!twitters[account]) {
          setTwitters((s) => ({ ...s, [account]: twitter }))
        }
      }
    }
  }, [debounced])

  useEffect(() => {
    if (account && confirmation) {
      if (confirmation.state.toLowerCase() === account.toLowerCase()) {
        console.log('show modal. congratulations')
      }
    }
  }, [account, confirmation])

  useTwitterAuth(account, !!confirmation)

  const handleRegister = async () => {
    if (!provider || !account) return
    let signature
    const timestamp = nowInSeconds()
    try {
      signature = await provider.getSigner().signMessage(
        JSON.stringify({
          timestamp,
        })
      )
    } catch (e) {
      console.warn(e)
      return
    }
    const url = `${process.env.REACT_APP_API_URL}/api/auth/twitter?timestamp=${timestamp}&signature=${signature}&account=${account}`
    window.open(url, '_blank', 'noreferrer')
  }

  if (!account || isTwitterBound) {
    return null
  }

  return (
    <Wrapper>
      {status === Status.PENDING ? (
        <Loader />
      ) : (
        <>
          <Label>Bind your Twitter account to open bonus tasks</Label>
          <ButtonPurple style={{ padding: '9px 20px', width: 'auto', gap: '0.5rem' }} onClick={handleRegister}>
            <Twitter /> <Trans>Sign & Authorize</Trans>
          </ButtonPurple>
        </>
      )}
    </Wrapper>
  )
}

export default BindTwitter
